import React from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';

import { PapiUser } from '@wix/da-types-papi';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconTrashcan from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';

import WidgetHeader from '../WidgetHeader';
import WidgetEditControls from '../WidgetEditControls';
import WidgetHeaderMobile from '../WidgetHeaderMobile';
import withConfirmationModal, {
  WithConfirmationModalProps,
} from '../../_hoc/withConfirmationModal';
import { GallectionType } from '../../../../../types/gallection';
import { ModuleType } from '../../../../../types/modules';
import { ZoneType } from '../../../../../types/layout';
import { moduleTypeToTranslationKey } from '../../../../../utils/modules';
import { UpdateModulePayload } from '../../../../actions/modules';
import { getViewAsVisitorUrl } from '../../../../utils/urls';
import WidgetBoxWrapper from '../WidgetBoxWrapper';

import s from './WidgetBox.scss';

export interface Props {
  moduleId: number;
  moduleType: ModuleType;
  moduleVersion: number;
  zone: ZoneType;
  section: string;
  children: React.ReactNode;
  className?: string;
  header: string | React.ReactNode;
  headerExtras?: React.ReactNode | ((props: any) => React.ReactNode);
  count?: number | string;
  allowEditOnMobile?: boolean;
  autoShowWidgetConfig?: boolean;
  isAuthorizedToEditWidget?: boolean;
  isAuthorizedToRemoveWidget?: boolean;
  isMobile?: boolean;
  isNewlyAddedModule?: boolean;
  isCurrentUserProfileOwner: boolean;
  isAuthorizedToMoveModule: boolean;
  isEmpty?: boolean;
  emptyView?: React.ReactNode;
  hasMore?: boolean;
  profileOwnerUser: PapiUser;
  seeAllUrl?: string;
  seeAllText?: string;
  widgetMenuRenderer?: (closeMenu) => React.ReactNode;
  widgetMoveUp?: () => void;
  widgetMoveDown?: () => void;
  widgetRemove?: () => void;
  editControlsTooltipTitle?: string;
  editControlsTooltipContent?: React.ReactNode;
  withFTUE?: boolean;
  updateModule?: (payload: UpdateModulePayload) => void;
  sourceType?: GallectionType;
  dataHook?: string;
  isViewingAsVisitor?: boolean;
}

export interface State {
  isWidgetConfigShown: boolean;
}

const enhance = compose(
  withMobileContext,
  withConfirmationModal,
  withTranslation()
);
export class WidgetBox extends React.Component<
  Props & WithTranslation & WithConfirmationModalProps,
  State
> {
  state = { isWidgetConfigShown: false };

  render() {
    const {
      moduleId,
      isMobile,
      className,
      header,
      count,
      children,
      isEmpty,
      emptyView,
      moduleType,
      seeAllUrl,
      dataHook,
      profileOwnerUser,
    } = this.props;

    if (isEmpty && !emptyView) {
      return null;
    }

    const headerContents = seeAllUrl ? this.renderSeeAllLink(header) : header;
    return (
      <BiLoggerContextProvider
        value={{ widgetname: moduleType, sectionname: moduleType }}
      >
        <WidgetBoxWrapper
          data-role="widget"
          className={classnames(s['root'], className)}
          data-hook={dataHook}
          data-moduleid={moduleId}
          data-userid={profileOwnerUser.userId}
        >
          <ErrorBoundary>
            {isMobile ? (
              <WidgetHeaderMobile
                header={headerContents}
                count={count}
                isSticky={false}
                extras={this.renderHeaderExtraElements()}
              />
            ) : (
              <WidgetHeader
                header={headerContents}
                count={count}
                extras={this.renderHeaderExtraElements()}
              />
            )}
            {isEmpty ? emptyView : children}
          </ErrorBoundary>
        </WidgetBoxWrapper>
      </BiLoggerContextProvider>
    );
  }

  renderSeeAllLink(contents, role?, className?) {
    const { seeAllUrl, profileOwnerUser, isViewingAsVisitor } = this.props;
    return seeAllUrl && contents ? (
      <BiLink
        biData={{
          evid: BiEventType.SEE_ALL_CLICK,
          creatorid: profileOwnerUser.useridUuid,
          typeid: undefined,
          itemid: undefined,
        }}
        href={isViewingAsVisitor ? getViewAsVisitorUrl(seeAllUrl) : seeAllUrl}
        {...(className && { className })}
        {...(role && { 'data-role': role })}
      >
        {contents}
      </BiLink>
    ) : null;
  }

  renderHeaderExtraElements() {
    const { headerExtras } = this.props;
    return (
      <div className={s['header-extras']}>
        {typeof headerExtras === 'function'
          ? headerExtras(this.props)
          : headerExtras}
        {this.renderEditControls()}
        {this.renderWidgetConfig()}
        {this.renderRightSideSeeAllLink()}
      </div>
    );
  }

  renderRightSideSeeAllLink() {
    const { t, hasMore, seeAllText, withFTUE } = this.props;

    const shouldRenderSeeAllLink = hasMore === undefined || hasMore;
    const contents = seeAllText || t!('common.see_all');
    const role = withFTUE ? 'ftue-control' : 'see-all-link';
    const linkClass = s['see-all'];
    return shouldRenderSeeAllLink
      ? this.renderSeeAllLink(contents, role, linkClass)
      : null;
  }

  renderEditControls() {
    const {
      isMobile,
      isCurrentUserProfileOwner,
      isAuthorizedToEditWidget,
      isAuthorizedToRemoveWidget,
      allowEditOnMobile,
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      widgetMenuRenderer,
      editControlsTooltipTitle,
      editControlsTooltipContent,
      isAuthorizedToMoveModule,
      withFTUE,
    } = this.props;

    const canUserMoveWidget =
      isCurrentUserProfileOwner || isAuthorizedToMoveModule;
    const isAuthorized =
      canUserMoveWidget ||
      isAuthorizedToRemoveWidget ||
      isAuthorizedToEditWidget;
    const isDisabledOnMobile = isMobile && !allowEditOnMobile;

    if (!isAuthorized || isDisabledOnMobile) {
      return null;
    }

    return (
      <WidgetEditControls
        className={s['edit-widget-controls']}
        onMoveUp={widgetMoveUp}
        onMoveDown={widgetMoveDown}
        onRemove={
          isAuthorizedToRemoveWidget && widgetRemove
            ? this.handleWidgetRemove
            : undefined
        }
        onEdit={
          isAuthorizedToEditWidget && widgetMenuRenderer
            ? this.showWidgetConfig
            : undefined
        }
        withFTUE={withFTUE}
        tooltipTitle={editControlsTooltipTitle}
        tooltipContent={editControlsTooltipContent}
      />
    );
  }

  renderWidgetConfig() {
    const { widgetMenuRenderer, autoShowWidgetConfig, isNewlyAddedModule } =
      this.props;
    const { isWidgetConfigShown } = this.state;

    if (!widgetMenuRenderer) {
      return null;
    }

    if (isWidgetConfigShown || (isNewlyAddedModule && autoShowWidgetConfig)) {
      return widgetMenuRenderer && widgetMenuRenderer(this.hideWidgetConfig);
    }
  }

  showWidgetConfig = () => {
    this.setState({ isWidgetConfigShown: true });
  };

  hideWidgetConfig = () => {
    const { isNewlyAddedModule, updateModule } = this.props;
    this.setState({ isWidgetConfigShown: false });
    isNewlyAddedModule &&
      updateModule &&
      updateModule({
        id: this.props.moduleId,
        moduleMeta: {
          isNewlyAdded: false,
        },
      });
  };

  handleWidgetRemove = () => {
    const { moduleType, widgetRemove, pushConfirmationModal, t } = this.props;
    const sectionName = t(moduleTypeToTranslationKey(moduleType));
    switch (moduleType) {
      case ModuleType.CUSTOM:
      case ModuleType.DONATIONS:
        // These will destroy content if removed, show warning
        pushConfirmationModal({
          title: t('widgets.common.removeWidget.header', {
            sectionName,
          }),
          text: t('widgets.common.removeWidget.body.danger'),
          confirmBtnLabel: t('widgets.common.removeWidget.confirmBtnLabel'),
          cancelBtnLabel: t('widgets.common.removeWidget.cancelBtnLabel'),
          icon: <IconTrashcan size={IconSize.MEGA} />,
          onConfirm: widgetRemove,
        });
        break;
      default:
        pushConfirmationModal({
          title: t('widgets.common.removeWidget.header', {
            sectionName,
          }),
          text: t('widgets.common.removeWidget.body'),
          confirmBtnLabel: t('widgets.common.removeWidget.confirmBtnLabel'),
          cancelBtnLabel: t('widgets.common.removeWidget.cancelBtnLabel'),
          icon: <IconTrashcan size={IconSize.MEGA} />,
          onConfirm: widgetRemove,
        });
        break;
    }
  };
}

export default enhance(WidgetBox) as React.ComponentType<Props>;
